import React, { FC, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { Box, Stack, Typography } from "@components/base";
import { Logo } from "@components/common";
import { useAuth } from "@hooks/useAuth";

const CsobAuthPage: FC = () => {
  const navigate = useNavigate();
  const { refreshTokenRequest } = useAuth();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const loginFailed = (): void => {
    enqueueSnackbar(t("csobAuth.loginFailedMessage"), {
      variant: "error",
      autoHideDuration: 5000,
    });
    navigate("/login");
  };

  const loginSuccess = (registrationAction: string): void => {
    enqueueSnackbar(t("csobAuth.loginSuccessMessage"), {
      variant: "success",
      autoHideDuration: 5000,
    });

    navigate(`/csob?registrationAction=${registrationAction}`);
  };

  const authRefreshToken = async (
    refreshToken: string,
    registrationAction: string,
  ): Promise<void> => {
    try {
      const response = await refreshTokenRequest(refreshToken);

      if (response.status === 200) {
        const { access_token, refresh_token } = response.data;

        localStorage.setItem("access_token", access_token);

        if (refresh_token) {
          localStorage.setItem("refresh_token", refresh_token);
        }

        window.dispatchEvent(new Event("access_token_change"));

        loginSuccess(registrationAction);
      } else {
        loginFailed();
      }
    } catch {
      loginFailed();
    }
  };

  const init = async (): Promise<void> => {
    const token = searchParams.get("token");
    const registrationAction = searchParams.get("registrationAction");

    if (token) {
      authRefreshToken(token, registrationAction || "1");
    } else {
      loginFailed();
    }
  };

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    init();
  }, []);

  return (
    <Stack justifyContent="center" alignItems="center" spacing={3} height="100vh">
      <Box width={160}>
        <Logo type="green0meter-with-csob" />
      </Box>
      <Typography variant="subtitle1" textAlign="center">
        {t("csobAuth.processingAuth")}
      </Typography>
    </Stack>
  );
};

export default CsobAuthPage;
